import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getBlurPlaceholder } from "@/utils/blurPlaceholders";

type GalleryImage = {
  src: string;
  alt?: string;
};

interface AutoScrollGalleryProps {
  id?: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  images: (string | GalleryImage)[];
  className?: string;
  showOverlayButton?: boolean;
  overlayButtonText?: string;
  scrollSpeed?: number; // seconds for one complete cycle
  imageHeight?: string; // height of each image
}

export const AutoScrollGallery: React.FC<AutoScrollGalleryProps> = ({
  id,
  eyebrow = "",
  title = "",
  subtitle = "",
  images,
  className = "",
  showOverlayButton = true,
  overlayButtonText = "View Image",
  scrollSpeed = 30,
  imageHeight = "h-80",
}) => {
  const resolved = images.map((img, index) =>
    typeof img === "string"
      ? { src: img, alt: `Gallery ${index + 1}` }
      : { src: img.src, alt: img.alt || `Gallery ${index + 1}` },
  );

  // Duplicate images for seamless scrolling
  const duplicatedImages = [...resolved, ...resolved];

  return (
    <section
      id={id}
      className={`py-24 bg-orange-50 ${className}`}
      data-oid="m3zbg6j"
    >
      <div className="container mx-auto px-6" data-oid="ql-c_tt">
        {(eyebrow || title || subtitle) && (
          <div className="text-center mb-8" data-oid=".yl:1zh">
            {eyebrow && (
              <div
                className="flex items-center justify-center mb-6"
                data-oid="_jgdys."
              >
                <div
                  className="w-8 h-0.5 bg-orange-500 mr-4"
                  data-oid="z2quamt"
                ></div>
                <p
                  className="text-sm font-bold tracking-[0.3em] text-orange-500"
                  data-oid="a9oi:7c"
                >
                  {eyebrow}
                </p>
                <div
                  className="w-8 h-0.5 bg-orange-500 ml-4"
                  data-oid="_6j8j3-"
                ></div>
              </div>
            )}
            {title && (
              <h2
                className="font-serif text-5xl font-bold mb-4"
                data-oid="-rvy:a1"
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-muted-foreground text-lg" data-oid="4oc_hvq">
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Full-width Auto-scrolling Gallery Container */}
      <div
        className="relative w-screen overflow-hidden"
        style={{
          marginLeft: "calc(-50vw + 50%)",
          marginRight: "calc(-50vw + 50%)",
        }}
        data-oid=":e4oswo"
      >
        <div
          className="flex space-x-6"
          style={{
            animation: `scroll ${scrollSpeed}s linear infinite`,
            width: `${duplicatedImages.length * 400}px`, // 400px per image (w-96 + gap-6)
          }}
          data-oid="wl9bm6m"
        >
          {duplicatedImages.map((image, index) => (
            <div
              key={`${image.src}-${index}`}
              className="relative group flex-shrink-0 w-96 overflow-hidden rounded-lg shadow-lg"
              data-oid="lghxpkn"
            >
              <OptimizedImage
                src={image.src}
                alt={image.alt}
                className={`w-full ${imageHeight} object-cover transform group-hover:scale-110 transition-transform duration-500`}
                blurDataURL={getBlurPlaceholder(image.src)}
                priority={false}
                fetchPriority="auto"
                rootMargin="200px"
                sizes="(max-width: 768px) 80vw, 33vw"
                data-oid="1c2_ry:"
              />

              {showOverlayButton && (
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  data-oid="h_o0jhs"
                >
                  <Button
                    variant="outline"
                    className="border-white text-white hover:bg-white hover:text-black"
                    data-oid="3zm37nc"
                  >
                    {overlayButtonText}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CSS Animation */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes scroll {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
          `,
        }}
        data-oid="1g.cv5j"
      />
    </section>
  );
};

export default AutoScrollGallery;
